import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Button, Chip, Grid,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  MenuItem, Alert, CircularProgress, Avatar, Tooltip, IconButton,
} from '@mui/material';
import {
  SwapHoriz, Add, Cancel, CheckCircle, HourglassEmpty,
  VerifiedUser, PersonOff, Warning,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

function apiFetch(path: string, opts: RequestInit = {}) {
  return fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4000'}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}`, ...(opts.headers || {}) },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

const STATUS_COLOR: Record<string, string> = {
  active: '#2e7d32', expired: '#78909c', cancelled: '#c62828',
};

const SCOPE_LABELS: Record<string, { label: string; color: string; emoji: string }> = {
  all:         { label: 'All Approvals',  color: '#4527a0', emoji: '🔑' },
  leave:       { label: 'Leave',          color: '#1565c0', emoji: '🌴' },
  expense:     { label: 'Expenses',       color: '#1b5e20', emoji: '💰' },
  overtime:    { label: 'Overtime',       color: '#e65100', emoji: '⏱️' },
  wfh:         { label: 'WFH',           color: '#00838f', emoji: '🏠' },
  toil:        { label: 'TOIL',          color: '#6a1b9a', emoji: '⏰' },
  recruitment: { label: 'Recruitment',   color: '#880e4f', emoji: '👤' },
};

interface Delegation {
  id: string;
  delegatorId: string;
  delegator?: { firstName: string; lastName: string; position?: { title: string } };
  delegateId: string;
  delegate?: { firstName: string; lastName: string; position?: { title: string } };
  startDate: string;
  endDate: string;
  scope: string;
  reason?: string;
  notes?: string;
  status: string;
  delegateAcknowledged: boolean;
  acknowledgedAt?: string;
  createdAt: string;
}

interface Stats { active: number; expiringSoon: number; total: number; byScope: { scope: string; count: string }[] }

const emptyForm = {
  delegateId: '', startDate: '', endDate: '',
  scope: 'all', reason: '', notes: '',
};

function empName(e?: { firstName: string; lastName: string }) {
  return e ? `${e.firstName} ${e.lastName}` : '—';
}

function isExpiringSoon(d: Delegation): boolean {
  if (d.status !== 'active') return false;
  const end = new Date(d.endDate);
  const diff = (end.getTime() - Date.now()) / 86400000;
  return diff <= 3;
}

export default function Delegation() {
  const { user } = useAuth();
  const isHR = ['hr_admin', 'hr_manager'].includes((user as any)?.role || '');

  const [tab, setTab] = useState(0);
  const [mine, setMine] = useState<Delegation[]>([]);
  const [forMe, setForMe] = useState<Delegation[]>([]);
  const [allActive, setAllActive] = useState<Delegation[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [employees, setEmployees] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [formData, setFormData] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const calls: Promise<any>[] = [
        apiFetch('/api/delegations/mine'),
        apiFetch('/api/delegations/for-me'),
        apiFetch('/api/employees?limit=200'),
      ];
      if (isHR) {
        calls.push(apiFetch('/api/delegations/active'));
        calls.push(apiFetch('/api/delegations/stats'));
      }
      const results = await Promise.allSettled(calls);
      if (results[0].status === 'fulfilled') setMine(results[0].value);
      if (results[1].status === 'fulfilled') setForMe(results[1].value);
      if (results[2].status === 'fulfilled') {
        const empData = results[2].value;
        setEmployees(Array.isArray(empData) ? empData : (empData.data ?? []));
      }
      if (results[3] && results[3].status === 'fulfilled') setAllActive((results[3] as any).value);
      if (results[4] && results[4].status === 'fulfilled') setStats((results[4] as any).value);
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }, [isHR]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setSaving(true);
    try {
      await apiFetch('/api/delegations', { method: 'POST', body: JSON.stringify(formData) });
      setFormOpen(false);
      load();
    } catch (e: any) { setError(e.message); }
    finally { setSaving(false); }
  };

  const cancel = async (id: string) => {
    try {
      await apiFetch(`/api/delegations/${id}/cancel`, { method: 'PATCH' });
      load();
    } catch (e: any) { setError(e.message); }
  };

  const acknowledge = async (id: string) => {
    try {
      await apiFetch(`/api/delegations/${id}/acknowledge`, { method: 'PATCH' });
      load();
    } catch (e: any) { setError(e.message); }
  };

  const incomingPending = forMe.filter(d => d.status === 'active' && !d.delegateAcknowledged);
  const today = new Date().toISOString().split('T')[0];
  const activeNow = forMe.filter(d => d.status === 'active' && d.startDate <= today && d.endDate >= today);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: '#4527a0', width: 48, height: 48 }}><SwapHoriz /></Avatar>
          <Box>
            <Typography variant="h5" fontWeight={700}>Approval Delegation</Typography>
            <Typography variant="body2" color="text.secondary">
              Delegate your approval authority when you're unavailable
            </Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => { setFormData({ ...emptyForm }); setFormOpen(true); }}
          sx={{ bgcolor: '#4527a0', '&:hover': { bgcolor: '#311b92' } }}>
          New Delegation
        </Button>
      </Box>

      {/* Active-now banner */}
      {activeNow.length > 0 && (
        <Alert severity="info" icon={<VerifiedUser />} sx={{ mb: 2 }}>
          <strong>Acting for {activeNow.map(d => empName(d.delegator)).join(', ')}</strong> — you currently have delegated approval authority
          {activeNow.map(d => ` (${SCOPE_LABELS[d.scope]?.label ?? d.scope}, until ${d.endDate})`).join('; ')}.
        </Alert>
      )}

      {/* Unacknowledged banner */}
      {incomingPending.length > 0 && (
        <Alert severity="warning" icon={<HourglassEmpty />} sx={{ mb: 2 }}>
          You have {incomingPending.length} unacknowledged delegation{incomingPending.length > 1 ? 's' : ''} — please review the <strong>Delegated to Me</strong> tab.
        </Alert>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Stats (HR) */}
      {isHR && stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Active Delegations', value: stats.active, color: '#2e7d32' },
            { label: 'Expiring in 3 Days', value: stats.expiringSoon, color: '#f57f17' },
            { label: 'Total Created', value: stats.total, color: '#4527a0' },
          ].map(s => (
            <Grid size={{ xs: 4 }} key={s.label}>
              <Paper sx={{ p: 2, textAlign: 'center', borderTop: `3px solid ${s.color}` }}>
                <Typography variant="h4" fontWeight={700} color={s.color}>{s.value}</Typography>
                <Typography variant="caption" color="text.secondary">{s.label}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
      ) : (
        <>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
            <Tab label={`My Delegations (${mine.length})`} />
            <Tab label={`Delegated to Me (${forMe.length})${incomingPending.length > 0 ? ` ⚠️` : ''}`} />
            {isHR && <Tab label={`All Active (${allActive.length})`} />}
          </Tabs>

          {/* My Delegations */}
          {tab === 0 && (
            <DelegationTable
              rows={mine}
              mode="delegator"
              onCancel={cancel}
            />
          )}

          {/* Delegated to Me */}
          {tab === 1 && (
            <DelegationTable
              rows={forMe}
              mode="delegate"
              onAcknowledge={acknowledge}
              onCancel={cancel}
            />
          )}

          {/* HR: All Active */}
          {isHR && tab === 2 && (
            <DelegationTable rows={allActive} mode="hr" onCancel={cancel} />
          )}
        </>
      )}

      {/* Create Dialog */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Approval Delegation</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <Alert severity="info">
            The selected colleague will be able to approve requests on your behalf during the specified period.
          </Alert>
          <TextField select fullWidth label="Delegate To" value={formData.delegateId}
            onChange={e => setFormData(p => ({ ...p, delegateId: e.target.value }))} required>
            {employees.map((e: any) => (
              <MenuItem key={e.id} value={e.id}>
                {e.firstName} {e.lastName}{e.position?.title ? ` — ${e.position.title}` : ''}
              </MenuItem>
            ))}
          </TextField>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField fullWidth label="From" type="date" InputLabelProps={{ shrink: true }}
              value={formData.startDate} onChange={e => setFormData(p => ({ ...p, startDate: e.target.value }))} required />
            <TextField fullWidth label="To" type="date" InputLabelProps={{ shrink: true }}
              value={formData.endDate} onChange={e => setFormData(p => ({ ...p, endDate: e.target.value }))} required />
          </Box>
          <TextField select fullWidth label="Approval Scope" value={formData.scope}
            onChange={e => setFormData(p => ({ ...p, scope: e.target.value }))}
            helperText="Which types of approvals to delegate">
            {Object.entries(SCOPE_LABELS).map(([k, v]) => (
              <MenuItem key={k} value={k}>{v.emoji} {v.label}</MenuItem>
            ))}
          </TextField>
          <TextField fullWidth label="Reason" value={formData.reason}
            onChange={e => setFormData(p => ({ ...p, reason: e.target.value }))}
            placeholder="e.g. Annual leave 20–27 Apr" />
          <TextField fullWidth label="Notes for Delegate (optional)" multiline rows={2}
            value={formData.notes} onChange={e => setFormData(p => ({ ...p, notes: e.target.value }))}
            placeholder="Any specific guidance for the person covering you" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={create} disabled={saving || !formData.delegateId || !formData.startDate || !formData.endDate}
            sx={{ bgcolor: '#4527a0' }}>
            {saving ? 'Creating…' : 'Create Delegation'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function DelegationTable({
  rows, mode, onCancel, onAcknowledge,
}: {
  rows: Delegation[];
  mode: 'delegator' | 'delegate' | 'hr';
  onCancel?: (id: string) => void;
  onAcknowledge?: (id: string) => void;
}) {
  const today = new Date().toISOString().split('T')[0];

  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {mode !== 'delegator' && <TableCell>Delegator</TableCell>}
            {mode !== 'delegate' && <TableCell>Delegate</TableCell>}
            <TableCell>Period</TableCell>
            <TableCell>Scope</TableCell>
            <TableCell>Reason</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Ack.</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                No delegations found
              </TableCell>
            </TableRow>
          )}
          {rows.map(d => {
            const expiring = isExpiringSoon(d);
            const isLive = d.status === 'active' && d.startDate <= today && d.endDate >= today;
            return (
              <TableRow key={d.id} hover sx={{ bgcolor: isLive ? '#f3e5f520' : undefined }}>
                {mode !== 'delegator' && (
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {d.delegator?.firstName} {d.delegator?.lastName}
                    </Typography>
                  </TableCell>
                )}
                {mode !== 'delegate' && (
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {d.delegate?.firstName} {d.delegate?.lastName}
                    </Typography>
                  </TableCell>
                )}
                <TableCell>
                  <Typography variant="body2">{d.startDate} → {d.endDate}</Typography>
                  {expiring && (
                    <Chip size="small" label="Expiring soon" icon={<Warning sx={{ fontSize: '0.75rem !important' }} />}
                      sx={{ bgcolor: '#fff3e0', color: '#e65100', fontSize: '0.65rem', height: 18, mt: 0.3 }} />
                  )}
                  {isLive && (
                    <Chip size="small" label="Active now"
                      sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontSize: '0.65rem', height: 18, mt: 0.3 }} />
                  )}
                </TableCell>
                <TableCell>
                  <Chip size="small"
                    label={`${SCOPE_LABELS[d.scope]?.emoji} ${SCOPE_LABELS[d.scope]?.label ?? d.scope}`}
                    sx={{ bgcolor: (SCOPE_LABELS[d.scope]?.color ?? '#999') + '20',
                      color: SCOPE_LABELS[d.scope]?.color ?? '#999', fontSize: '0.7rem' }} />
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">{d.reason || '—'}</Typography>
                </TableCell>
                <TableCell>
                  <Chip size="small" label={d.status}
                    sx={{ bgcolor: STATUS_COLOR[d.status] + '20', color: STATUS_COLOR[d.status], fontWeight: 600 }} />
                </TableCell>
                <TableCell>
                  {d.delegateAcknowledged
                    ? <Tooltip title={`Acknowledged ${d.acknowledgedAt ? new Date(d.acknowledgedAt).toLocaleDateString() : ''}`}>
                        <CheckCircle color="success" fontSize="small" />
                      </Tooltip>
                    : <Tooltip title="Not yet acknowledged">
                        <HourglassEmpty color="warning" fontSize="small" />
                      </Tooltip>
                  }
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {mode === 'delegate' && !d.delegateAcknowledged && d.status === 'active' && onAcknowledge && (
                      <Tooltip title="Acknowledge">
                        <IconButton size="small" color="success" onClick={() => onAcknowledge(d.id)}>
                          <CheckCircle fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {d.status === 'active' && onCancel && (
                      <Tooltip title="Cancel delegation">
                        <IconButton size="small" color="error" onClick={() => onCancel(d.id)}>
                          <Cancel fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
